import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Tabs from "../../../Components/Tabs";
import DataTable from "react-data-table-component";
import { createColumns } from "../../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../../constants/datatableStyles";
import CustomPagination from "../../../Components/CustomPagination";
import PageTitle from "../../../Components/UI/PageTitle";
import RowLoader from "../../../Components/UI/RowLoader";
import {
  deactivateClientsAccounts,
  getExtensionPeriod,
  getRenewSubscriptionList,
} from "../../../apis";
import Swal from "sweetalert2";
import { transformKeys } from "../../../_helper";
import ErrorAnimationComponent from "../../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import axios from "axios";
import { CLIENT_SUBSCRIPTION_CATEGORIES } from "../../../constants/globals";

const DATA_TABS = [
  {
    key: "Soon",
    label: "Soon",
  },
  {
    key: "extend_one",
    label: "Extend (1)",
  },
  {
    key: "extend_two",
    label: "Extend (2)",
  },
  {
    key: "Expired",
    label: "Expired",
  },
  {
    key: "re_activate_subscription",
    label: "Re Activate Subscription",
  },
];
const SubscriptionRenewalList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isExportLoading, setExportLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState([]);
  const [expiredAccountsData, setExpiredAccountsData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [currentExtensionPeriod, setCurrentExtensionPeriod] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!confirm) {
      setSubmitted(true); // to show error
      return;
    }
    Swal.fire({
      title: "Deactivate Accounts",
      html: `
      <p>Do you really want to deactivate the selected accounts?</p>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      showLoaderOnConfirm: true,
      preConfirm: () => {
        const data = {
          clientIds: selectedUserIds,
          confirm: confirm ? 1 : 0,
        };
        return deactivateClientsAccounts(data)
          .then((response) => response.data)
          .catch((error) => {
            console.error("Error deactivate accounts:", error);
            Swal.showValidationMessage(
              error.response.data.message || "Failed to deactivate accounts."
            );
          });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Good!", "Accounts deactivated successfully.", "success");

        // refetch data here
        fetchData();
      }
    });
  };

  const [currentTab, setCurrentTab] = useState(
    new URLSearchParams(location.search).get("tab") || "Soon"
  );

  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  const currentData = currentTab === "Expired" ? expiredAccountsData : data;

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(
      (prevSelected) =>
        prevSelected.includes(userId)
          ? prevSelected.filter((id) => id !== userId) // Remove
          : [...prevSelected, userId] // Add
    );
  };
  // get extened period
  useEffect(() => {
    const extensionMap = {
      extend_one: "firstExtensionPeriod",
      extend_two: "secondExtensionPeriod",
    };

    const extendPeriod = extensionMap[currentTab];

    if (!extendPeriod) return; // Exit if tab is not extend_one or extend_two

    getExtensionPeriod(extendPeriod)
      .then((response) => {
        const days = response?.data?.data?.days;
        if (typeof days === "number") {
          setCurrentExtensionPeriod(days);
        } else {
          console.warn("Unexpected extension period data format.");
        }
      })
      .catch((error) => {
        console.error("Failed to fetch extension period:", error);
      });
  }, [currentTab]);

  useEffect(() => {
    if (
      currentTab === "Expired" &&
      expiredAccountsData?.length > 0 &&
      selectedUserIds?.length === 0
    ) {
      const defaultSelectedIds = expiredAccountsData?.map(
        (user) => user.client_id
      );
      setSelectedUserIds(defaultSelectedIds);
    }
  }, [currentTab, expiredAccountsData]);

  const fetchData = () => {
    const endpoint = CLIENT_SUBSCRIPTION_CATEGORIES[currentTab];

    if (!endpoint) {
      console.warn("Invalid tab key:", currentTab);
      return;
    }

    setIsLoading(true);
    getRenewSubscriptionList(endpoint, currentPage, searchTerm)
      .then((response) => {
        const rawData = response?.data?.data || [];
        const handleData = rawData.map(transformKeys);

        if (currentTab === "Expired") {
          setExpiredAccountsData(handleData);
        } else {
          setData(handleData);
        }

        const paginator = response?.data?.paginator || {};
        setPaginator({
          current_page: paginator.current_page || 1,
          last_page: paginator.last_page || 1,
        });
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setError(error.message || "Failed to fetch data");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [currentTab, currentPage, searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", currentTab);
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [currentTab, searchTerm, currentPage, sortKey, sortDirection, navigate]);

  const handleTabClick = (key) => {
    setCurrentTab(key);
    setCurrentPage(1); // Reset to first page on tab change
  };

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column, direction) => {
    setSortKey(column.sortKey);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };
  const handleExport = async () => {
    setExportLoading(true);

    try {
      const { data } = await axios.get(
        `/admin/renewSubscription/list/export/${CLIENT_SUBSCRIPTION_CATEGORIES[currentTab]}`,
        {
          responseType: "blob",
        }
      );
      const fileName = `Renew_Subscription_List_For_${CLIENT_SUBSCRIPTION_CATEGORIES[currentTab]}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const hiddenColumns = ["id", "client_id"]; // Add column keys you want to hide

  const customColumns =
    currentTab === "Expired"
      ? [
          {
            name: "Select",
            selector: (row) => row.name,
            ui: (row) => (
              <div className="tw-flex tw-justify-center">
                <input
                  type="checkbox"
                  value={row.client_id}
                  checked={selectedUserIds.includes(row.client_id)}
                  onChange={() => toggleUserSelection(row.client_id)}
                />
              </div>
            ),
          },
        ]
      : [
          "Soon",
          "extend_one",
          "extend_two",
          "re_activate_subscription",
        ].includes(currentTab)
      ? [
          {
            name: "Setting",
            selector: (row) => row.name + " - custom",
            ui: (row) => (
              <div className="tw-flex tw-gap-1">
                <Link
                  className="tw-text-primary"
                  to={`/renew-client-subscription/${row.client_id}`}
                >
                  Renew
                </Link>
              </div>
            ),
          },
        ]
      : [];

  const columns = useMemo(() => {
    return createColumns(currentData, customColumns, hiddenColumns, true).map(
      (col) => ({
        ...col,
        // sortable: col.name === "Name" || col.name === "Joined Date",
        sortable: false,
        width:
          col.name === "Num" ? "60px" : col.name === "Name" ? "150px" : "1fr",
      })
    );
  }, [data, selectedUserIds, currentData]);

  if (error) return <ErrorAnimationComponent message={error} />;

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-between">
        <PageTitle title="Renew Subscription" />
        {["extend_one", "extend_two"].includes(currentTab) &&
          currentExtensionPeriod && (
            <div className="tw-mt-2 tw-mb-4">
              <span className="tw-inline-block tw-bg-[#eee] tw-text-primary tw-text-sm tw-font-medium tw-px-4 tw-py-1.5 tw-rounded-full tw-shadow-sm tw-border tw-border-primary">
                Extension period: {currentExtensionPeriod} days
              </span>
            </div>
          )}
      </div>
      <div className="bg-white flex flex-col mt-4">
        <Tabs
          options={DATA_TABS}
          onTabClick={handleTabClick}
          currentTab={currentTab}
        />

        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-3">
          <div className="relative">
            <div className="tw-mb-2">
              <input
                type="text"
                onChange={handleFilter}
                value={searchTerm}
                className="border border-blue-300 rounded-lg p-2 col-md-12 tw-mb-2"
                placeholder="Search by name & email and phone"
              />
            </div>
          </div>
          <DataTable
            columns={columns}
            data={currentData}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          {currentTab === "Expired" ? (
            <></>
          ) : (
            <div className="flex justify-center mt-4">
              <CustomPagination
                totalPages={paginator?.last_page}
                currentPage={paginator?.current_page || 1}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      {currentTab === "Expired" && (
        <div className="tw-flex xl:tw-flex-row tw-flex-col tw-gap-2 tw-justify-between tw-items-center !tw-my-4">
          {/* Checkbox to Confirm */}
          <div className="tw-flex tw-gap-2 tw-items-center">
            <input
              type="checkbox"
              id="confirm"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
              className="tw-mr-2 tw-w-4 !tw-mb-1"
            />
            <label
              htmlFor="confirm"
              className="tw-text-sm tw-cursor-pointer !tw-my-0"
            >
              I confirm that I will deactivate all selected accounts and remove
              any remaining balance from their wallets.
            </label>
            {/* Error if not confirmed */}
            {!confirm && submitted && (
              <p className="tw-text-red-500 tw-text-xs tw-mt-1">
                You must confirm before submitting.
              </p>
            )}
          </div>

          <button
            disabled={!confirm || selectedUserIds?.length === 0}
            onClick={handleSubmit}
            className="!tw-bg-primary btn tw-text-white !tw-w-[150px] hover:!tw-bg-primary/90 hover:tw-text-white disabled:tw-text-white"
          >
            {submitted ? (
              <RowLoader paddingStyle="tw-py-0" />
            ) : (
              "Deactivate Now"
            )}
          </button>
        </div>
      )}
      {data?.length > 0 && (
        <button
          className={`tw-border tw-bg-green-600/20 tw-flex tw-justify-center tw-w-[200px] tw-h-10 tw-text-center tw-border-green-500 tw-px-4 tw-py-2 tw-rounded-lg tw-items-center tw-gap-2 tw-transition-all hover:tw-bg-green-600/30`}
          onClick={handleExport}
        >
          {isExportLoading ? (
            <div className="tw-flex tw-justify-center">
              <RowLoader paddingStyle="tw-py-0" />
            </div>
          ) : (
            <>
              <img
                src="/imgs/excelIcon.png"
                className="tw-size-5"
                alt="Export"
              />
              <span>Export as excel file</span>
            </>
          )}
        </button>
      )}
    </>
  );
};

export default SubscriptionRenewalList;
